import { useState, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { 
  START_MULTIPART_UPLOAD, 
  GET_MULTIPART_PRESIGNED_URL, 
  COMPLETE_MULTIPART_UPLOAD,
  GET_S3_UPLOAD_PRESIGNED_URL,
  type PartInput,
  type StartMultipartUploadData,
  type GetMultipartPreSignedUrlData,
  type CompleteMultipartUploadData,
  type GetS3UploadPresignedUrlData,
  type StartMultipartUploadVariables,
  type GetMultipartPreSignedUrlVariables,
  type CompleteMultipartUploadVariables,
} from '@jwp/ott-common/src/graphql/query/reels.queries';
import {
  ADD_MEDIA_APPROVAL,
  EDIT_MEDIA_APPROVAL,
  type MediaApprovalInput,
  type MediaApprovalPatch,
  type AddMediaApprovalData,
  type EditMediaApprovalData
} from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import { 
  ADD_VIDEO_MEDIA, 
  type AddVideoMediaData, 
  type VideoMediaInput, 
  type AdditionalImageInput 
} from '@jwp/ott-common/src/graphql/mutation/video.mutation';
import { GET_ALL_DROPDOWNS, type GetAllDropdownsData, type GetAllDropdownsVariables } from '@jwp/ott-common/src/graphql/query/video.queries';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { generateS3Key } from '@jwp/ott-common/src/utils/s3';
import { extractThumbnailFromVideo } from '@jwp/ott-common/src/utils/mediaUtils';

import { useAppConfig } from './useAppConfig';

// const CHUNK_SIZE = 15 * 1024 * 1024; // 15MB chunks

/**
 * Common headers for ETag that might be returned by S3 depending on CORS configuration
 */
const extractETag = (response: Response): string | null => {
  return response.headers.get('ETag')?.replaceAll('"', '') || 
         response.headers.get('Etag')?.replaceAll('"', '') || 
         response.headers.get('etag')?.replaceAll('"', '') ||
         null;
};

export type UploadStatus = 'idle' | 'uploading' | 'completed' | 'failed';

export const useMultipartUpload = () => {
  const { multipartUploadChunkSize } = useAppConfig();
  const CHUNK_SIZE = (multipartUploadChunkSize || 15) * 1024 * 1024;

  const [status, setStatus] = useState<UploadStatus>('idle');

  const [error, setError] = useState<string | null>(null);

  const user = useAccountStore((state) => state.user);


  const [addMediaApproval] = useMutation<AddMediaApprovalData, { input: MediaApprovalInput }>(ADD_MEDIA_APPROVAL);
  const [editMediaApproval] = useMutation<EditMediaApprovalData, { input: MediaApprovalPatch }>(EDIT_MEDIA_APPROVAL);
  const [addVideoMedia] = useMutation<AddVideoMediaData, { input: VideoMediaInput }>(ADD_VIDEO_MEDIA);
  
  const [startMultipartUpload] = useLazyQuery<StartMultipartUploadData, StartMultipartUploadVariables>(START_MULTIPART_UPLOAD);
  const [getMultipartPreSignedUrl] = useLazyQuery<GetMultipartPreSignedUrlData, GetMultipartPreSignedUrlVariables>(GET_MULTIPART_PRESIGNED_URL);
  const [completeMultipartUpload] = useLazyQuery<CompleteMultipartUploadData, CompleteMultipartUploadVariables>(COMPLETE_MULTIPART_UPLOAD);
  const [getS3UploadPresignedUrl] = useLazyQuery<GetS3UploadPresignedUrlData, { key: string }>(GET_S3_UPLOAD_PRESIGNED_URL);
  const [getAllDropdowns] = useLazyQuery<GetAllDropdownsData, GetAllDropdownsVariables>(GET_ALL_DROPDOWNS);

  const uploadSingleFile = useCallback(async (file: Blob | File, key: string, label?: string) => {
    const { data: presignedData } = await getS3UploadPresignedUrl({
      variables: { key }
    });

    if (!presignedData) throw new Error(`Failed to get presigned URL for ${label || 'file'}`);
    
    const url = presignedData?.getS3UploadPresignedUrl?.preSignedUrl;
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'image/jpeg' }
    });

    if (!response.ok) {
      throw new Error(`Failed to upload ${label || 'file'}`);
    }
  }, [getS3UploadPresignedUrl]);

  const uploadThumbnail = useCallback(async (thumbnailFile: Blob | File, thumbnailKey: string) => {
    await uploadSingleFile(thumbnailFile, thumbnailKey, 'thumbnail');
  }, [uploadSingleFile]);

  const uploadChunks = useCallback(async (file: File, mediaKey: string, uploadId: string, totalParts: number): Promise<PartInput[]> => {
    const parts: PartInput[] = [];

    for (let i = 1; i <= totalParts; i++) {
      const start = (i - 1) * CHUNK_SIZE;
      const end = Math.min(i * CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const { data: presignedData } = await getMultipartPreSignedUrl({
        variables: {
          key: mediaKey,
          partNumber: i,
          uploadId
        }
      });

      if (!presignedData) throw new Error(`Failed to get presigned URL for part ${i}`);
      
      const response = await fetch(presignedData.getS3MultipartPreSignedUrl.preSignedUrl, {
        method: 'PUT',
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload part ${i}`);
      }

      const eTag = extractETag(response);
      if (!eTag) {
        throw new Error(`No ETag returned for part ${i}. Please verify that "ExposeHeaders": ["ETag"] is set in your S3 Bucket CORS rules.`);
      }

      parts.push({ partNumber: i, eTag });
    }

    return parts;
  }, [getMultipartPreSignedUrl, CHUNK_SIZE]);

  const buildSidecarFilesMetadata = useCallback(async (
    staticThumbKey: string | null,
    motionThumbKey: string | null,
    additionalAssets: { key: string; labelId: string; labelName: string; labelValue: string }[]
  ): Promise<AdditionalImageInput[]> => {
    const { data: dropdownData } = await getAllDropdowns({
      variables: {
        first: 10,
        offset: 0,
        filter: {
          ddType: { equalTo: 'IMAGE_TYPE' },
          ddValue: { in: ['static', 'video'] },
          ddDeletedAt: { isNull: true }
        }
      }
    });

    const imageTypes = dropdownData?.allDropdowns?.nodes || [];
    const staticType = imageTypes.find(t => t.ddValue === 'static');
    const motionType = imageTypes.find(t => t.ddValue === 'video');

    const finalAdditionalImages: AdditionalImageInput[] = [];

    if (staticThumbKey && staticType) {
      finalAdditionalImages.push({
        maAdditionalImagesS3KeyUrl: staticThumbKey,
        maAdditionalImagesLabelId: staticType.ddId,
        maAdditionalImagesLabelName: staticType.ddLabel,
        maAdditionalImagesLabelValue: staticType.ddValue,
        maIsThumbnail: true
      });
    }

    if (motionThumbKey && motionType) {
      finalAdditionalImages.push({
        maAdditionalImagesS3KeyUrl: motionThumbKey,
        maAdditionalImagesLabelId: motionType.ddId,
        maAdditionalImagesLabelName: motionType.ddLabel,
        maAdditionalImagesLabelValue: motionType.ddValue,
        maIsThumbnail: true
      });
    }

    additionalAssets.forEach(asset => {
      finalAdditionalImages.push({
        maAdditionalImagesS3KeyUrl: asset.key,
        maAdditionalImagesLabelId: asset.labelId,
        maAdditionalImagesLabelName: asset.labelName,
        maAdditionalImagesLabelValue: asset.labelValue,
        maIsThumbnail: false
      });
    });

    return finalAdditionalImages;
  }, [getAllDropdowns]);

  const uploadSidecarAssets = useCallback(async (
    staticThumbnail?: File,
    staticThumbKey?: string | null,
    motionThumbnail?: File,
    motionThumbKey?: string | null,
    additionalAssets: { file: File; key: string; labelName: string }[] = []
  ) => {
    const sidecarUploads = [
      ...(staticThumbnail && staticThumbKey ? [{ file: staticThumbnail, key: staticThumbKey, label: 'Static Thumbnail' }] : []),
      ...(motionThumbnail && motionThumbKey ? [{ file: motionThumbnail, key: motionThumbKey, label: 'Motion Thumbnail' }] : []),
      ...additionalAssets.map(asset => ({ file: asset.file, key: asset.key, label: asset.labelName }))
    ];

    for (const { file, key, label } of sidecarUploads) {
      await uploadSingleFile(file, key, label);
    }
  }, [uploadSingleFile]);

  const uploadFile = useCallback(async (file: File, metadata: Omit<MediaApprovalInput, 'maS3MediaKeyUrl' | 'maS3ThumbnailKeyUrl'>, customThumbnail?: File) => {
    if (!user?.id) {
      setError('User not logged in');
      setStatus('failed');
      return;
    }

    let mediaId: string | null = null;
    try {
      setStatus('uploading');
      let thumbnailFile: Blob | File | undefined = customThumbnail;
      thumbnailFile ??= await extractThumbnailFromVideo(file);

      const mediaKey = generateS3Key(user.id, file.name, 'reels');
      const thumbnailKey = generateS3Key(user.id, file.name, 'thumbnails');

      const { data: approvalData } = await addMediaApproval({
        variables: {
          input: {
            ...metadata,
            maS3MediaKeyUrl: mediaKey,
            maS3ThumbnailKeyUrl: thumbnailKey,
          }
        }
      });

      if (!approvalData) throw new Error('Failed to create media approval');
      mediaId = approvalData.addMediaApproval.mediaId;

      const { data: startData } = await startMultipartUpload({
        variables: {
          contentType: file.type,
          key: mediaKey
        }
      });

      if (!startData) throw new Error('Failed to start multipart upload');
      const uploadId = startData?.startS3MultipartUpload?.uploadId;
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);
      const parts = await uploadChunks(file, mediaKey, uploadId, totalParts);

      if (thumbnailFile) {
        await uploadThumbnail(thumbnailFile, thumbnailKey);
      }

      const { data: completeData } = await completeMultipartUpload({
        variables: {
          key: mediaKey,
          uploadId,
          parts,
          maId: mediaId
        }
      });

      if (!completeData) throw new Error('Failed to complete multipart upload');

      setStatus('completed');
      return mediaId;

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      console.error('Upload failed:', errorMessage);

      if (mediaId) {
        try {
          await editMediaApproval({
            variables: {
              input: {
                maId: mediaId,
                maUploadStatus: 'FAILED'
              }
            }
          });
        } catch (editErr) {
          console.error('Failed to update upload status to FAILED:', editErr);
        }
      }

      setError('upload failed');
      setStatus('failed');
    }
  }, [user, addMediaApproval, editMediaApproval, startMultipartUpload, completeMultipartUpload, uploadThumbnail, uploadChunks, CHUNK_SIZE]);


  const uploadVideo = useCallback(async (
    videoFile: File,
    metadata: Omit<VideoMediaInput, 'maS3MediaKeyUrl' | 'maS3ThumbnailKeyUrl' | 'maAdditionalImages'>,
    motionThumbnail?: File,
    staticThumbnail?: File,
    additionalImages: { file: File; labelId: string; labelName: string; labelValue: string }[] = []
  ) => {
    if (!user?.id) {
      setError('User not logged in');
      setStatus('failed');
      return;
    }

    try {
      setStatus('uploading');

      const videoKey = generateS3Key(user.id, videoFile.name, 'videos', 'videos');
      const staticThumbKey = staticThumbnail ? generateS3Key(user.id, staticThumbnail.name, 'thumbnails', 'videos') : null;
      const motionThumbKey = motionThumbnail ? generateS3Key(user.id, motionThumbnail.name, 'thumbnails', 'videos') : null;
      
      const additionalAssets = additionalImages.map(img => ({
        ...img,
        key: generateS3Key(user.id, img.file.name, 'additional-images', 'videos')
      }));

      // 1. Prepare Metadata
      const finalAdditionalImages = await buildSidecarFilesMetadata(staticThumbKey, motionThumbKey, additionalAssets);

      // 2. Create Media Record
      const { data: videoData } = await addVideoMedia({
        variables: {
          input: {
            ...metadata,
            maType: 'VIDEOS',
            maS3MediaKeyUrl: videoKey,
            maS3ThumbnailKeyUrl: staticThumbKey || '',
            maAdditionalImages: finalAdditionalImages,
          }
        }
      });

      if (!videoData) throw new Error('Failed to create video media');
      const maId = videoData.addVideoMedia.mediaId;

      // 3. Video Multipart Upload
      const { data: startData } = await startMultipartUpload({
        variables: { contentType: videoFile.type, key: videoKey }
      });

      if (!startData) throw new Error('Failed to start multipart upload');
      const uploadId = startData.startS3MultipartUpload.uploadId;
      const parts = await uploadChunks(videoFile, videoKey, uploadId, Math.ceil(videoFile.size / CHUNK_SIZE));

      // 4. Sidecar Assets Upload
      await uploadSidecarAssets(staticThumbnail, staticThumbKey, motionThumbnail, motionThumbKey, additionalAssets);

      // 5. Completion
      const { data: completeData } = await completeMultipartUpload({
        variables: { key: videoKey, uploadId, parts, maId }
      });

      if (!completeData) throw new Error('Failed to complete multipart upload');

      setStatus('completed');
      return maId;

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Video upload failed';
      console.error('Video upload failed:', errorMessage);
      setError(errorMessage);
      setStatus('failed');
    }
  }, [user, addVideoMedia, buildSidecarFilesMetadata, startMultipartUpload, uploadChunks, uploadSidecarAssets, completeMultipartUpload, CHUNK_SIZE]);

  return {
    uploadFile,
    uploadVideo,
    status,
    error,
    reset: () => {
      setStatus('idle');
      setError(null);
    }
  };
};
